import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SchoolManagement.css';

export default function SchoolManagement() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }, []);

  return (
    <div className="school-management-page">
      {/* Header with Login Button */}
      <header className="top-header">
        <div className="header-container">
          <Link to="/" className="logo-section">
            <img src="/AstraVision_Logo.png" alt="Astravision" />
            <span className="logo-text">Astravision</span>
          </Link>
          <button className="login-btn" onClick={() => window.location.href = 'https://school.astravision.net'}>
            <i className="fas fa-sign-in-alt"></i> Login
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <img src="/absloute-logo.jpeg" alt="AbsoluteVision Logo" style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px' }} />
              <span style={{ fontSize: '22px', fontWeight: '600', color: '#fff' }}>Absolute Vision</span>
            </div>
            <h1 className="hero-title">School Management System</h1>
            <p className="hero-subtitle">
              Transform your educational institution with Astravision's comprehensive school management solution. 
              Streamline operations, enhance learning experiences, and empower every stakeholder.
            </p>
            <button className="hero-btn" onClick={() => navigate('/')}>
              Get Started Today <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
        <div className="wave-decoration">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64L48,58.7C96,53,192,43,288,48C384,53,480,75,576,80C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" 
                  fill="rgba(255, 255, 255, 0.1)"/>
          </svg>
        </div>
      </section>

      {/* Main Content */}
      <div className="container">
        {/* About Us Section */}
        <section className="section">
          <h2 className="section-title">About Us</h2>
          <p className="section-subtitle">
            Astravision is a leading provider of innovative education technology solutions, 
            dedicated to transforming the way schools operate and students learn.
          </p>
          
          <div className="about-grid">
            <div className="about-content">
              <h3>Who We Are</h3>
              <p>
                Founded with a vision to revolutionize education management, Astravision Softech Private Limited 
                specializes in creating cutting-edge software solutions that empower educational institutions worldwide.
              </p>
              <p>
                Our team of experienced educators, technologists, and innovators work tirelessly to develop 
                platforms that simplify complex administrative tasks, enhance teaching methodologies, and 
                create engaging learning environments for students of all ages.
              </p>
              <p>
                With thousands of schools trusting our platform, we continue to set new standards in 
                educational technology through continuous innovation and unwavering commitment to excellence.
              </p>
            </div>
            
            <div className="about-image">
              <i className="fas fa-school"></i>
            </div>
          </div>
        </section>

        {/* Vision & Mission Section */}
        <section className="section">
          <h2 className="section-title">Vision & Mission</h2>
          <p className="section-subtitle">
            Our guiding principles that drive innovation and excellence in education technology
          </p>
          
          <div className="vm-grid">
            <div className="vm-card">
              <div className="vm-icon">
                <i className="fas fa-eye"></i>
              </div>
              <h3>Our Vision</h3>
              <p>
                To become the world's most trusted education technology platform, empowering every school 
                to deliver exceptional learning experiences. We envision a future where technology seamlessly 
                integrates with education, making quality learning accessible to every student, everywhere.
              </p>
              <p style={{marginTop: '20px'}}>
                We strive to create an ecosystem where administrators, teachers, students, and parents 
                collaborate effortlessly, driven by data-driven insights and innovative tools that foster 
                academic excellence and holistic development.
              </p>
            </div>
            
            <div className="vm-card">
              <div className="vm-icon">
                <i className="fas fa-bullseye"></i>
              </div>
              <h3>Our Mission</h3>
              <p>
                To deliver innovative, user-friendly, and scalable school management solutions that transform 
                educational institutions into centers of excellence. We are committed to simplifying complex 
                administrative processes and enhancing teaching-learning outcomes through technology.
              </p>
              <p style={{marginTop: '20px'}}>
                Our mission is to continuously innovate, listen to our users, and develop features that 
                address real-world challenges faced by schools, ensuring sustainable growth and measurable 
                impact on student success.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="section">
          <h2 className="section-title">Comprehensive Features</h2>
          <p className="section-subtitle">
            Everything your school needs in one powerful platform
          </p>
          
          <div className="features-grid">
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-graduation-cap"></i>
              </div>
              <h4>Smart Learning</h4>
              <p>AI-powered personalized learning paths</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-chalkboard-teacher"></i>
              </div>
              <h4>Teacher Portal</h4>
              <p>Complete tools for educators</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-user-graduate"></i>
              </div>
              <h4>Student Hub</h4>
              <p>Centralized student access</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h4>Analytics</h4>
              <p>Data-driven insights</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h4>Secure Access</h4>
              <p>Enterprise-grade security</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-cloud"></i>
              </div>
              <h4>Cloud Based</h4>
              <p>Access anytime, anywhere</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-comments"></i>
              </div>
              <h4>Communication</h4>
              <p>Seamless collaboration</p>
            </button>
            
            <button className="feature-card" onClick={() => navigate('/school-management/all-features')}>
              <div className="feature-icon">
                <i className="fas fa-bus"></i>
              </div>
              <h4>Transport Tracking</h4>
              <p>Real-time GPS monitoring</p>
            </button>
          </div>
        </section>

        {/* CTA Section */}
        <div className="cta-section">
          <h2>Ready to Transform Your School?</h2>
          <p>Join thousands of institutions worldwide using Astravision</p>
          <button className="cta-btn" onClick={() => navigate('/')}>
            Get Started Today
          </button>
        </div>
      </div>
    </div>
  );
}
